// Polyfill Object.hasOwn for Safari < 15.4 (ES2022, absent on macOS Catalina)
if (!Object.hasOwn) {
  // eslint-disable-next-line prefer-object-has-own
  Object.hasOwn = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
}

global.$ = global.jQuery = require('jquery');

// daterangepicker must be synchronous: DatatablesLoader.js calls .daterangepicker() at module
// level (before any async code runs), so the plugin must already be on jQuery when it loads.
require('daterangepicker');
require('daterangepicker/daterangepicker.css');

// Synchronous $notify stub — must be defined before any Twig inline <script> tags fire.
// Twig flash messages and other inline scripts call window.$notify() immediately after
// the bundle loads, before the async Promise.all resolves and TwToastPlugin installs.
// This stub queues those calls; toastService.flush() drains the queue on mount.
window.__notifyQueue = window.__notifyQueue || [];
window.$notify =
  window.$notify ||
  function (opts, type) {
    window.__notifyQueue.push(typeof opts === 'string' ? { message: opts, type: type || 'info' } : opts);
  };

// iOS WebKit messageHandlers defensive wrapper
// Prevent TypeError: "undefined is not an object (evaluating 'window.webkit.messageHandlers[e].postMessage')"
if (typeof window !== 'undefined') {
  if (!window.webkit) {
    window.webkit = {};
  }
  if (!window.webkit.messageHandlers) {
    window.webkit.messageHandlers = {};
  }

  // Create a Proxy to handle dynamic property access
  window.webkit.messageHandlers = new Proxy(window.webkit.messageHandlers, {
    get: function (target, property) {
      if (target[property]) {
        return target[property];
      }
      // Return a mock handler with postMessage method for any undefined handler
      // IMPORTANT: Must return a Promise to prevent Facebook Pixel's undefined.then() error
      return {
        postMessage: function () {
          return Promise.resolve(null);
        },
      };
    },
  });
}

// Deferred modules — start downloading immediately alongside the core boot,
// but do not block Vue from mounting. Initialized after mount in the .then() below.
// - bootstrap-datepicker: jQuery plugin for Twig date-only pickers, not needed by Vue
// - redactor: rich text editor for the experience builder; has its own webpack entry (RedactorLoader)
// Note: daterangepicker is required synchronously above — DatatablesLoader calls it at module level.
const deferredBoot = Promise.all([
  import('bootstrap-datepicker'),
  import('bootstrap-datepicker/dist/css/bootstrap-datepicker.standalone.min.css'),

  import('@fortawesome/fontawesome-pro/css/all.min.css'),

  import('select2/dist/css/select2.css'),

  import('vue-select/dist/vue-select.css'),
  import('vue-multiselect/dist/vue-multiselect.min.css'),

  require('animate.css'),
  import('bootstrap'),
  require('bootbox'),
  import('pretty-checkbox/dist/pretty-checkbox.min.css'),
  import('expose-loader?exposes=bootbox!bootbox'),

  require('ag-grid-community/styles/ag-grid.css'),
  require('ag-grid-community/styles/ag-theme-quartz.css'),

  // for collections in forms - https://github.com/ninsuo/symfony-collection
  require('../../vendor/ninsuo/symfony-collection/jquery.collection.js'),

  // jquery timeago
  require('../../vendor/rmm5t/jquery-timeago/jquery.timeago.js'),

  // used by the experience builder
  import('./vendor/redactor/redactor.min.js'),
]);

// Core boot — everything Vue needs to mount.
// MainMenu and LogRocket are imported directly in App.vue; no need to load them here.
Promise.all([
  import('vue'), // 0
  import('vue-mq'), // 1
  import('vue-router'), // 2
  import('@bugsnag/js'), // 3
  import('@bugsnag/plugin-vue'), // 4
  import('vue-moment'), // 5
  import('moment-timezone'), // 6
  import('select2'), // 7
  import('vue-numeral-filter'), // 8
  import('./routes.js'), // 9
  import('axios'), // 10
  import('vue-axios'), // 11
  import('vue-plugin-load-script'), // 12
  import('pusher-js'), // 13
  import('./App.vue'), // 14
  import('@vue/composition-api'), // 15
  import('./directives/translatable'), // 16
  import('./i18n'), // 17
  import('pinia'), // 18
  import('./mixpanel'), // 19
  import('./plugins/ui-library'), // 20 — TripWorks UI Component Library
  import('./components/shared/VueFilters'), // 21 — side-effect: registers Vue filters globally

  import('bootstrap-vue/dist/bootstrap-vue.css'), // 22
  import('@fortawesome/fontawesome-pro/css/all.min.css'), // 23
  import('select2/dist/css/select2.css'), // 24
  import('vue-select/dist/vue-select.css'), // 25
  import('vue-multiselect/dist/vue-multiselect.min.css'), // 26
  require('bootstrap-notify'), // 27
  require('animate.css'), // 28
  import('bootstrap'), // 29
  import('pretty-checkbox/dist/pretty-checkbox.min.css'), // 30
  import('expose-loader?exposes=bootbox!bootbox'), // 31
  import('./directives/requiresConnection'), // 32 — side-effect: registers v-requires-connection (TRIP-2666)
  import('./directives/touchEvents'), // 33 — side-effect: registers v-touch
]).then((modules) => {
  const Vue = modules[0].default;
  const VueMq = modules[1].default;
  const vueRouter = modules[2].default;
  const Bugsnag = modules[3].default;
  const BugsnagPluginVue = modules[4].default;
  const vueMoment = modules[5].default;
  const moment = modules[6].default;
  const select2 = modules[7].default;
  const vueNumeralFilterInstaller = modules[8].default;
  const vueRoutes = modules[9].default;
  const axios = modules[10].default;
  const vueAxios = modules[11].default;
  const LoadScript = modules[12].default;
  const Pusher = modules[13].default;
  const App = modules[14].default;
  const compositionApi = modules[15];
  const translation = modules[16];
  const i18nInstance = modules[17].default;
  const { createPinia, PiniaVuePlugin } = modules[18];
  const mixpanel = modules[19].default;
  const uiLibrary = modules[20].default;

  Vue.use(translation);
  Vue.use(mixpanel);
  // UI library, toasts, the List/Detail kit, Pinia, moment and the XHR header, shared with the Sophia entry.
  require('./plugins/installSharedVue').installSharedVue({ Vue, axios, uiLibrary, PiniaVuePlugin, vueMoment, moment });

  // TRIP-2666: the offline "region" boundary, available everywhere so any
  // data-dependent area (modal body, grid, sidebar, tab pane) can degrade
  // gracefully without a per-screen import. See docs/claude/frontend/offline-ui.md.
  // Registered SYNCHRONOUSLY (not a lazy () => import): the component and its
  // scoped CSS must ride the main bundle so they're cached and usable OFFLINE —
  // a lazy chunk would fail to load in the exact offline state it exists to handle.
  Vue.component('OfflineBoundary', require('./components/shared/OfflineBoundary.vue').default);

  const pinia = createPinia();

  select2($);

  // pusher
  const pp = new Pusher(process.env.PUSHER_KEY, {
    cluster: 'us2',
    forceTLS: true,
  });

  Vue.prototype.$pusher = pp;

  Vue.use(LoadScript);

  // will likely need to be installed elsewhere after we know the locale
  // locale can be passed as second parameter - see https://www.npmjs.com/package/vue-numeral-filter
  Vue.use(vueNumeralFilterInstaller);

  Vue.use(VueMq, {
    breakpoints: {
      mobile: 490,
      tablet: 1250,
      // 		laptop: 1250,
      desktop: Infinity,
    },
  });

  const { scrollBehavior } = require('./helpers/routerScrollBehavior');
  const router = new vueRouter({
    mode: 'history',
    routes: vueRoutes,
    scrollBehavior,
  });

  // Swallowed by design, so the trace is the only way a future stale-callback bug gets noticed.
  const { patchRouterNavigationFailures, traceSwallowedFailure } = require('./helpers/patchRouterNavigationFailures');
  patchRouterNavigationFailures(router, vueRouter.isNavigationFailure, traceSwallowedFailure(Bugsnag));

  // TRIP-2666: while offline, only routes explicitly marked `offlineCapable`
  // (today just the Manifest, which has a cached data layer) may be navigated to.
  // Everything else is online-only, so block the in-app navigation. Inverting
  // the allowlist means new online-only screens are safe by default without
  // per-route annotation. (Cold full-page loads self-handle via the SW's
  // offline.html; this guard is only for client-side SPA navigations.)
  // Registered BEFORE the title guard so a blocked nav aborts before the tab
  // title is changed.
  const { isAppOffline } = require('./composables/useOfflineDetection');
  router.beforeEach((to, from, next) => {
    const offlineCapable = to.matched.some((r) => r.meta && r.meta.offlineCapable);
    if (!isAppOffline.value || offlineCapable) {
      next();
      return;
    }
    if (from && from.name) {
      // Explain right at the offline indicator: OfflineNavIndicator listens for
      // this and opens its popover. A bottom toast proved too easy to miss
      // (PM feedback on TRIP-2666).
      window.dispatchEvent(new CustomEvent('tw:offline-nav-blocked'));
      next(false); // stay on the current working screen
    } else {
      window.location.href = '/offline.html'; // no screen to stay on (cold load) → offline screen
    }
  });

  // Are we on a server-rendered (legacy Twig) page rather than the Vue SPA shell? base.html.twig
  // emits the #spa-shell marker only in its default `body` block; legacy pages override that block,
  // so the marker is absent on them. This is the same signal App.vue's isInVue uses to decide whether
  // to render <router-view>. The document can't change without a reload, so compute it once.
  const isServerRenderedPage = !document.getElementById('spa-shell');
  const { shouldHandOffToFullLoad } = require('./helpers/spaNavigationGuard');

  // change the meta title
  router.beforeEach((to, from, next) => {
    if (shouldHandOffToFullLoad(isServerRenderedPage, to, from)) {
      // We're on a legacy Twig page, so the SPA never renders a <router-view> (isInVue is false) —
      // a router-link click (top nav, global search, etc.) would silently change the URL without
      // navigating. Hand off to a real page load of the target Vue route instead. See
      // shouldHandOffToFullLoad for why the Twig page is detected via #spa-shell, not from.name.
      // Abort the pending SPA navigation first so vue-router's lock is released before we leave —
      // defends against the (rare) case where the full load stalls or is cancelled and would
      // otherwise leave the router pending.
      next(false);
      // The pre-redirect path, so the reloaded SPA re-runs the redirect and keeps `redirectedFrom` (bare /calendar).
      window.location.href = to.redirectedFrom || to.fullPath;
    } else {
      const nearestWithTitle = to.matched
        .slice()
        .reverse()
        .find((r) => r.meta && r.meta.title);
      if (nearestWithTitle) document.title = nearestWithTitle.meta.title;
      next();
    }
  });

  // NOTE TO UPGRADE axios to 1.0 or higher.
  // You will need to change the X-Requested-With line in plugins/installSharedVue.js to:
  // 	axios.default.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
  // This needs to be done everywhere within the app.
  // Further Some of the vue files will need the axios calls to be changed to this.axios
  // I had everything working but ran into issues getting the widget builder to work properly.
  // There must be something I don't understand about how the js files interact with Vue and
  // how axios should be loaded.

  Vue.use(vueRouter);

  // Wrap axios with SWR cache layer BEFORE vue-axios registers it.
  // vue-axios defines Vue.prototype.axios as a non-configurable getter,
  // so we must wrap first and let vue-axios expose the wrapped version.
  const { wrapAxiosWithCache } = require('./composables/useQueryWithCache');
  const cachedAxios = wrapAxiosWithCache(axios);

  // Offline awareness (TRIP-2666): write-gate + reachability marking on the
  // raw instance, AFTER wrapAxiosWithCache (see offlineInterceptor.js header
  // for the interceptor ordering contract).
  const { installOfflineInterceptors } = require('./helpers/offlineInterceptor');
  installOfflineInterceptors(axios);

  // Offline app-shell service worker — registered/unregistered from the
  // localStorage flag mirror after boot work settles; App.vue re-syncs when
  // app_init refreshes the flag. NOTE: this module runs from an async
  // bootstrap, so the window `load` event has usually ALREADY fired — check
  // readyState instead of only listening.
  const { syncServiceWorkerRegistration, primeManifestOffline } = require('./offline/serviceWorkerRegistration');
  if (document.readyState === 'complete') {
    syncServiceWorkerRegistration();
  } else {
    window.addEventListener('load', () => syncServiceWorkerRegistration());
  }

  require('./offline/logoutPurge').purgeIfSignedOut();
  // A full page load primes the manifest offline layer via register(); this
  // covers arriving at the manifest via a SOFT SPA navigation (no page load and
  // no `navigate` request), which is otherwise never primed and leaves the
  // offline refresh with an uncached shell. No-ops off the manifest / flag off. (TRIP-2666)
  router.afterEach(() => primeManifestOffline());

  Vue.use(vueAxios, cachedAxios);

  Vue.use(compositionApi);

  router.afterEach((_to, _from) => {
    window.Appcues && window.Appcues.page();
  });

  // load up BugSnag
  if (process.env.NODE_ENV !== 'development') {
    if (process.env.BUGSNAG_KEY) {
      Bugsnag.start({
        apiKey: process.env.BUGSNAG_KEY,
        plugins: [new BugsnagPluginVue()],
        releaseStage: process.env.APP_ENV === 'prod' ? 'production' : process.env.APP_ENV,
        enabledReleaseStages: ['production', 'stage'],
        appVersion: process.env.DEPLOY_COMMIT || 'unknown',
        redactedKeys: [/api_key/i, /token/i, /secret/i],
        onError: (event) => {
          // Mobile Safari can dispatch a trusted DOM Event as a promise rejection reason
          // instead of a proper Error. Bugsnag reports these as "InvalidError:
          // unhandledrejection handler received a non-error". Drop them — they are not
          // real application errors.
          const originalError = event.originalError;
          if (originalError && originalError.isTrusted === true) {
            return false;
          }
          return true;
        },
      });

      // Add deployment metadata
      Bugsnag.addMetadata('deployment', {
        branch: process.env.DEPLOY_BRANCH || 'unknown',
        commit: process.env.DEPLOY_COMMIT || 'unknown',
        timestamp: process.env.DEPLOY_TIMESTAMP || '',
        environment: process.env.APP_ENV || 'unknown',
      });

      Bugsnag.getPlugin('vue').installVueErrorHandler(Vue);
      Vue.use(Bugsnag);

      // help Bugsnag
      router.afterEach((to, from) => {
        if (to.name) {
          Bugsnag.leaveBreadcrumb('Navigate to route ' + to.name, {
            from: {
              name: from.name,
              meta: from.meta,
              hash: from.hash,
              query: from.query,
              fullPath: from.fullPath,
            },
            to: {
              name: to.name,
              meta: to.meta,
              hash: to.hash,
              query: to.query,
              fullPath: to.fullPath,
            },
          });
        }
      });
    } else {
      console.log('Cannot load Bugsnag; BUGSNAG_KEY variable not found in the env file.');
    }
  }

  // Test Bugsnag source maps in stage environment - throws unhandled exception
  if (window.location.search.includes('testBugsnag')) {
    setTimeout(() => {
      console.log('Tripworks Bugsnag test: Throwing unhandled exception to test source maps in stage environment');
      // This will throw an unhandled exception to test Bugsnag source map integration
      throw new Error(
        'Bugsnag source map test - Unhandled exception in stage environment at ' + new Date().toISOString(),
      );
    }, 3000); // Wait 3 seconds after app initialization
  }

  // app.js loads on every page via base.html.twig, but not every Symfony-rendered page
  // has a #app mount point (e.g. photo gallery, standalone tools). Guard before mounting.
  if (document.getElementById('app')) {
    new Vue({
      router: router,
      pinia,
      i18n: i18nInstance,
      render: (h) => h(App),
    }).$mount('#app');
  }

  // start all the global javascript functions here
  $('.deleteForm').submit(function (e) {
    e.preventDefault();
    const currentForm = this;
    const message = $(this).attr('data-confirmation-message')
      ? $(this).attr('data-confirmation-message')
      : 'Please confirm that you wish to delete this object.';
    bootbox.confirm({
      title: $(this).attr('data-confirmation-title') ? $(this).attr('data-confirmation-title') : 'Delete this item?',
      closeButton: false,
      centerVertical: true,
      onEscape: true,
      onEnter: true,
      message: message,
      callback: function (result) {
        if (result) {
          currentForm.submit();
        }
      },
      buttons: {
        cancel: {
          label: 'Cancel',
          className: 'btn-default',
        },
        confirm: {
          label: 'Delete Now',
          className: 'btn-danger',
        },
      },
    });
  });

  $('.deleteMe').click(function (e) {
    e.preventDefault();
    const actionURL = $(this).attr('action');
    const message = $(this).attr('data-confirmation-message')
      ? $(this).attr('data-confirmation-message')
      : 'Please confirm that you wish to delete this object.';
    bootbox.confirm({
      title: $(this).attr('data-confirmation-title') ? $(this).attr('data-confirmation-title') : 'Delete this item?',
      closeButton: false,
      centerVertical: true,
      onEscape: true,
      onEnter: true,
      message: message,
      callback: function (result) {
        if (result) window.location.href = actionURL;
      },
      buttons: {
        cancel: {
          label: 'Cancel',
          className: 'btn-default',
        },
        confirm: {
          label: 'Delete Now',
          className: 'btn-danger',
        },
      },
    });
  });

  jQuery('input, select')
    .not('[type=radio]')
    .on('invalid', function (_event) {
      if ($(this).attr('required') !== 'required') return false;

      if ($(this).hasClass('markedRequired')) return false;

      $(this).closest('.form-group').addClass('has-error has-danger');

      $(this).after(
        "<p class='text-danger required-warning'><small><i class='fa fa-asterisk'></i>&nbsp;Required.</small></p>",
      );

      if ($(this).is(':hidden')) {
        const tab = $(this).closest('.tab-pane');

        if (tab.length > 0) {
          const tabIndex = $(tab).index();
          const navLink = $('.nav-item').eq(tabIndex).children('.nav-link');

          if (navLink.find('.fa-exclamation-triangle').length === 0) {
            navLink.prepend("<i class='text-danger fas fa-exclamation-triangle tab-required-warning'></i>");
          }
          $(navLink).tab('show');
        }
      }

      $('html, body').animate({ scrollTop: 0 }, 'slow');

      $(this).addClass('markedRequired');
    });

  jQuery('input.is-invalid, input.is-invalid:disabled').each(function (_index) {
    $(this).closest('.form-group').addClass('has-error has-danger');

    if ($(this).is(':hidden') || $(this).is(':disabled')) {
      const tab = $(this).closest('.tab-pane');

      if (tab.length > 0) {
        const tabId = tab.attr('id');
        const navLink = $('.nav-link[href="#' + tabId + '"]');

        if (navLink.length > 0 && navLink.find('.fa-exclamation-triangle').length === 0) {
          navLink.prepend("<i class='text-danger fas fa-exclamation-triangle tab-required-warning'></i> ");
        }
        $(navLink).tab('show');
      }
    }
  });

  // make the bottom form action float — floats .form-actions bar when page is taller than viewport
  function FormActionFloater() {
    if ($(window).height() + 100 < $(document).height()) {
      jQuery('.form-actions').addClass('float');
    } else {
      jQuery('.form-actions').removeClass('float');
    }
  }

  // adds data-toggle="tooltip" to info icons on Twig forms that don't already have it
  function Tooltips() {
    $(function () {
      $('i.fa-info-circle[title]:not([data-toggle])').attr('data-toggle', 'tooltip');
      $('[data-toggle="tooltip"]').tooltip({ delay: { show: 700, hide: 100 } });
    });
  }

  jQuery('[type=submit]').on('click', function () {
    $('.required-warning').remove();
    $('.tab-required-warning').remove();
    $('.markedRequired').removeClass('markedRequired');
  });

  // load filters for datatables
  // this looks for a hidden form on the page, moves inputs to a footer row

  Tooltips();

  // initialize the select2 boxes in form controls
  jQuery('select.select2.form-control:not(.no-init)').each(function () {
    jQuery(this).select2({
      placeholder: jQuery(this).attr('placeholder') ? jQuery(this).attr('placeholder') : 'Choose...',
      allowClear: jQuery(this).hasClass('allow_blank'),
      width: 'resolve',
      closeOnSelect: true, //jQuery(this).attr('multiple') ? true : true
    });

    if (jQuery(this).hasClass('disabled')) {
      jQuery(this).select2('readonly', true);
    }
  });

  jQuery('.todo-sidebar-section-sub-section-toggler').click(function () {
    $(this).siblings('.todo-sidebar-section-sub-section-content').toggleClass('open');
  });

  // Custom radio picker used in Symfony forms with class="checkbox-expanded"
  // Clicking an .element-box visually selects it and syncs the underlying radio input state
  $('.expanded-radio-picker:not([disabled]) .element-box').on('click', function () {
    $(this).siblings().removeClass('selected');
    $(this).addClass('selected');
    $(this).siblings().find('input[type="radio"]').attr('checked', false);
    $(this).find('input[type="radio"]').attr('checked', true);
  });

  $('.menu-activated-on-click').on('click', 'li.has-sub-menu > a', function (_event) {
    const $elem = $(this).closest('li');
    if ($elem.hasClass('active')) {
      $elem.removeClass('active');
    } else {
      $elem.closest('ul').find('li.active').removeClass('active');
      $elem.addClass('active');
    }
    return false;
  });

  // make the save/cancel buttons fixed
  jQuery(document).ready(function () {
    FormActionFloater();
  });

  // make the save/cancel buttons fixed
  $(window).resize(function () {
    FormActionFloater();
  });

  // if they toggle bootstrap tabs, trigger the form action floater
  $('a[data-toggle="tab"]').on('shown.bs.tab', function (_e) {
    FormActionFloater();
  });

  // 		type: 'success',
  // 		allow_dismiss: false,
  // 		placement: {
  // 			from: "bottom",
  // 			align: "right"
  // 		},
  // 		template: '<div data-notify="container" class="alert alert-{0}" role="alert">' +
  // 			'<button type="button" aria-hidden="true" class="close" data-notify="dismiss">×</button>' +
  // 			'<span data-notify="icon"></span> ' +
  // 			'<span data-notify="title">{1}</span> ' +
  // 			'<span data-notify="message">{2}</span>' +
  // 			'<div class="progress" data-notify="progressbar">' +
  // 				'<div class="progress-bar progress-bar-{0}" role="progressbar" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100" style="width: 0%;"></div>' +
  // 			'</div>' +
  // 			'<a href="{3}" target="{4}" data-notify="url"></a>' +
  // 			'</div>'
});

// Deferred init — runs after Vue mounts, once daterangepicker and redactor have loaded.
// These modules are non-essential for the initial render and are initialized here
// so they don't delay the Vue mount above.
deferredBoot.then(() => {
  jQuery('.datetime-picker').daterangepicker({
    singleDatePicker: true,
    timePicker: true,
  });
});

// Application CSS — loaded via webpack (HMR injection in dev, extracted bundle in prod)
require('../../public/css/theme-main.css');
require('../../public/css/core.css');
require('../../public/css/mainmenu.css');
require('../../public/css/tripbuilder.css');
require('../../public/css/ag-grid.css');
require('../../public/css/email.css');
require('../../public/css/customer-portal.css');
require('../../public/css/trip.css');
require('../../public/css/call.css');
require('../../public/css/pos.css');
require('../../public/css/reseller.css');
require('../../public/css/invoice.css');
require('../../public/css/dashboard.css');
require('../../public/css/reports.css');
require('../../public/css/calendar.css');
require('../../public/css/manifest.css');
require('../../public/css/integrations.css');
require('../../public/css/ecomPluginBuilder.css');
require('../../public/css/widgets/bookNowButton.css');
require('../../public/css/trip-builder-widget.css');
require('../../public/css/importer.css');
require('ag-grid-community/styles/ag-grid.css');
require('ag-grid-community/styles/ag-theme-quartz.css');

// Sticky sidebar for integration detail pages
// (CSS position:sticky is broken by overflow:hidden on <html>)
document.addEventListener('DOMContentLoaded', function () {
  const page = document.querySelector('.integration-detail');
  if (!page) return;

  const sidebar = page.querySelector('.col-4 .about, .col-lg-4 > div');
  if (!sidebar) return;

  const col = sidebar.parentElement;
  const offset = 72; // 52px nav + 20px breathing room
  sidebar.style.position = 'relative';

  function onScroll() {
    const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
    const colTop = col.getBoundingClientRect().top + scrollTop;
    const maxTop = col.offsetHeight - sidebar.offsetHeight;
    const newTop = Math.min(Math.max(scrollTop - colTop + offset, 0), maxTop);
    sidebar.style.top = newTop + 'px';
  }

  document.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
});
